import type { DailyScoringProfile, DailyScoringProfileId } from "./types";

/**
 * Adaptive Daily Points: `dailyPoints = finalLength * lengthCoefficient +
 * floor(activePlayMs / 1000) * survivalPointsPerSecond + score *
 * scoreCoefficient` - a deterministic integer formula (every input and
 * every coefficient is an integer; `Math.floor` on the one division
 * involved removes any fractional-second ambiguity), so it can never
 * disagree between two machines, and the backend can always exactly
 * reproduce whatever the client displayed. `dailyPoints` is only ever
 * meaningful *within* one challenge day (a different day can use a
 * different profile/different coefficients entirely) - see the PR
 * description's "only needs to be comparable within the same challenge
 * day" rule.
 *
 * Coefficients were chosen from this game's actual scoring ranges (see
 * `GameConfig.bites`), not picked arbitrarily:
 *   - `score` (biteScore + skillScore): SAFE=15, RUSH escalates
 *     20/35/50/65/80 per streak position, CHAOS JACKPOT=60, SPEED TRIAL
 *     reward=80 - a representative extended survival run mixing these
 *     lands roughly in the 200-1500 range.
 *   - `finalLength`: starts at 3-14 (recipe-dependent) and, given no
 *     target/gate to stop growth, can climb well past that over a long
 *     run - roughly 10-60 for a solid run, higher on an open/growth-heavy
 *     map.
 *   - `activePlayMs`/1000 (active survival seconds): a short attempt is
 *     single-digit seconds; a strong run against a gentle pressure ramp
 *     can run 60-180+ seconds before the ramp/a mistake ends it.
 *
 * Each profile's coefficients were picked so, against those
 * representative ranges, EVERY term stays clearly positive and
 * meaningful (never negligible, per the "no profile may completely
 * ignore length, time or score" rule) while the profile's own named
 * emphasis (survival/growth/score) is the term that comes out on top for
 * a typical run of that shape. See `docs`/README's Daily Points section
 * for the worked example numbers.
 */
export const DAILY_SCORING_PROFILES: Record<DailyScoringProfileId, DailyScoringProfile> = {
  balanced: {
    id: "balanced",
    label: "BALANCED",
    lengthCoefficient: 25,
    survivalPointsPerSecond: 5,
    scoreCoefficient: 1,
  },
  survival_heavy: {
    id: "survival_heavy",
    label: "SURVIVAL-HEAVY",
    lengthCoefficient: 15,
    survivalPointsPerSecond: 12,
    scoreCoefficient: 1,
  },
  growth_heavy: {
    id: "growth_heavy",
    label: "GROWTH-HEAVY",
    lengthCoefficient: 45,
    survivalPointsPerSecond: 3,
    scoreCoefficient: 1,
  },
  score_heavy: {
    id: "score_heavy",
    label: "SCORE-HEAVY",
    lengthCoefficient: 15,
    survivalPointsPerSecond: 4,
    scoreCoefficient: 2,
  },
};

export interface DailyRawResultMetrics {
  finalLength: number;
  activePlayMs: number;
  score: number;
}

/**
 * Bounds every submitted raw metric must satisfy before `dailyPoints` is
 * ever computed from them - shared by the client (a UX nicety only) and
 * the server (`daily-result.ts`, the real boundary; matches
 * `record_daily_result()`'s own SQL-level bounds so the two can never
 * silently disagree). Generous relative to any real run (the grid is
 * 18x18 = 324 cells, so `finalLength` can never legitimately approach
 * `maxFinalLength`), never a source of false rejections for a genuine
 * result.
 */
export const DAILY_RESULT_BOUNDS = {
  minFinalLength: 1,
  maxFinalLength: 2000,
  minActivePlayMs: 0,
  maxActivePlayMs: 6 * 60 * 60 * 1000,
  minScore: -100_000,
  maxScore: 100_000,
} as const;

export function isValidDailyRawMetrics(metrics: DailyRawResultMetrics): boolean {
  const { finalLength, activePlayMs, score } = metrics;
  const b = DAILY_RESULT_BOUNDS;
  return (
    Number.isInteger(finalLength) &&
    finalLength >= b.minFinalLength &&
    finalLength <= b.maxFinalLength &&
    Number.isInteger(activePlayMs) &&
    activePlayMs >= b.minActivePlayMs &&
    activePlayMs <= b.maxActivePlayMs &&
    Number.isInteger(score) &&
    score >= b.minScore &&
    score <= b.maxScore
  );
}

/**
 * The one place `dailyPoints` is ever computed from a profile + raw
 * metrics - called both client-side (display only) and server-side (the
 * authoritative value actually stored; see `daily-result.ts`). Never
 * takes a client-supplied `dailyPoints` value as input - only the raw
 * metrics and the challenge's own immutable stored profile, so the
 * result can never be anything other than what the formula says for
 * those two inputs.
 */
export function computeDailyPoints(profile: DailyScoringProfile, metrics: DailyRawResultMetrics): number {
  const activeSeconds = Math.floor(metrics.activePlayMs / 1000);
  return metrics.finalLength * profile.lengthCoefficient + activeSeconds * profile.survivalPointsPerSecond + metrics.score * profile.scoreCoefficient;
}

/** The honest, concise "TODAY'S SCORING" rule text shown before an attempt - e.g. "LENGTH × 25 · SURVIVAL +5/sec · SCORE × 1". Never a percentage (the formula isn't normalized to one). */
export function formatScoringRuleText(profile: DailyScoringProfile): string {
  return `LENGTH × ${profile.lengthCoefficient} · SURVIVAL +${profile.survivalPointsPerSecond}/sec · SCORE × ${profile.scoreCoefficient}`;
}
